import React, { useContext, useState } from 'react';

import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { ChannelsContext, UserDataContext } from 'lane-shared/contexts';
import {
  FONT_AWESOME_REGULAR,
  FONT_AWESOME_SOLID,
  ICON_SET_FONTAWESOME,
} from 'constants-activate';
import { getDisplayName } from 'lane-shared/helpers';
import { useFlag } from 'lane-shared/hooks';
import { Channel, UserSettingsType } from 'application-types';
import { FeatureFlag } from 'constants-flags';
import { setDefaultChannelMutation } from 'graphql-queries';
import { Icon } from 'design-system-web';

import Button from '../general/Button';
import { HomeChannelPickerModal } from './HomeChannelPickerModal';
import { datadogRum } from '@datadog/browser-rum';
import styles from './HomeChannelSettings.scss';

function hasDefaultChannelId(
  settings: UserSettingsType | undefined | null
): settings is UserSettingsType {
  return (
    typeof settings === 'object' &&
    settings !== null &&
    'defaultChannelId' in settings
  );
}

export function HomeChannelSettings() {
  const { t } = useTranslation();
  const isHomeChannelSelectionEnabled = useFlag(
    FeatureFlag.UserHomeChannelSelection,
    false
  );
  const { user, refetch: refetchUser } = useContext(UserDataContext);
  const { channels } = useContext(ChannelsContext);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [setDefaultChannel] = useMutation(setDefaultChannelMutation);

  if (!isHomeChannelSelectionEnabled || channels.length < 2) {
    return null;
  }

  const settings = user?.settings;
  const homeChannelId = hasDefaultChannelId(settings)
    ? settings.defaultChannelId
    : undefined;
  const homeChannel = channels.find(
    (channel: Channel) => channel._id === homeChannelId
  );

  async function setHomeChannel(channel: Channel) {
    await setDefaultChannel({ variables: { channelId: channel._id } });
    await refetchUser().catch(error => {
      datadogRum.addError(error, {
        component: 'setHomeChannel',
        action: 'refetchUser',
      });
    });
  }

  return (
    <>
      <div className={styles.HomeChannelSettings} data-test="homeChannelSettings">
        <div className={styles.label}>
          <Icon
            className={styles.icon}
            name="home"
            set={ICON_SET_FONTAWESOME}
            type={homeChannelId ? FONT_AWESOME_SOLID : FONT_AWESOME_REGULAR}
          />
          {homeChannel ? (
            <span className={styles.channelName}>
              {getDisplayName(homeChannel)}
            </span>
          ) : (
            <span className={styles.placeholder}>
              {t('web.userProfile.homeChannel.noHomeChannelSet')}
            </span>
          )}
        </div>
        <Button
          variant="text"
          onClick={() => setIsPickerOpen(true)}
          testId="homeChannelCTA"
        >
          {homeChannel
            ? t('web.userProfile.homeChannel.change')
            : t('web.userProfile.homeChannel.setHomeChannel')}
        </Button>
      </div>
      <HomeChannelPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        channels={channels}
        currentHomeChannelId={homeChannelId}
        onConfirm={setHomeChannel}
      />
    </>
  );
}
